import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AppSelectionContext = createContext();

export const useAppSelection = () => {
    const context = useContext(AppSelectionContext);
    if (!context) {
        throw new Error("useAppSelection must be used within an AppSelectionProvider");
    }
    return context;
};

const STORAGE_KEY = "paycoinz_selected_app";

export const AppSelectionProvider = ({ children }) => {
    const [selectedApp, setSelectedAppState] = useState(() => {
        // Initialize from localStorage
        return localStorage.getItem(STORAGE_KEY) || "";
    });
    const [apps, setApps] = useState([]);
    const [appsLoading, setAppsLoading] = useState(false);

    // Persist to localStorage whenever selection changes
    const setSelectedApp = (appId) => {
        setSelectedAppState(appId);
        if (appId) {
            localStorage.setItem(STORAGE_KEY, appId);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    };

    // Listen for storage changes from other tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY) {
                setSelectedAppState(e.newValue || "");
            }
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    // Fetch apps list
    const fetchApps = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        setAppsLoading(true);
        try {
            const resp = await axios.get(
                `${process.env.REACT_APP_API_URL}/apps/get`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const fetchedApps = resp.data?.data || [];
            setApps(fetchedApps);

            // If no app selected yet, or selected app no longer exists, select the first one
            if (fetchedApps.length > 0) {
                const currentExists = fetchedApps.some((a) => a._id === selectedApp);
                if (!selectedApp || !currentExists) {
                    setSelectedApp(fetchedApps[0]._id);
                }
            }
        } catch (err) {
            console.error("Error fetching apps:", err);
        } finally {
            setAppsLoading(false);
        }
    };

    return (
        <AppSelectionContext.Provider
            value={{
                selectedApp,
                setSelectedApp,
                apps,
                appsLoading,
                fetchApps,
            }}
        >
            {children}
        </AppSelectionContext.Provider>
    );
};

export default AppSelectionContext;
